import { createContext } from "react";

import { CapabilitySet } from "@/types/capability.types";
import { OrganizationReference } from "@/types/organization.types";
import { noop } from "@/utils/function.utils";

/**
 * The resolved current league. `owner` is the league's owning organization — used to
 * preselect the org when a league is chosen (league ⇒ org). `title` / `logo` mirror the
 * nullable shape of the REST `LeagueRef`. `enableRankings` / `enableFactSheet` gate the
 * corresponding modules (nav items + onboarding steps) — surfaced on the ref so the
 * gating needs no second fetch. `capabilities` is the current user's capability set on
 * this league's LEAGUE node (RBAC) — empty until the BE ships it on the ref, so
 * capability guards fail closed (deny) under strict RBAC.
 */
export type ResolvedLeague = {
  id: string;
  title: string | null;
  logo: string | null;
  owner: OrganizationReference | null;
  enableRankings: boolean;
  enableFactSheet: boolean;
  capabilities: CapabilitySet;
};

export type CurrentLeagueContextType = {
  league: ResolvedLeague | null;
  loading: boolean;
  isError: boolean;
  /**
   * Select a league (navigating to the full league URL, preselecting the owning org)
   * or clear it with `null`. `ownerOrganizationId` lets the caller supply the league's
   * owner up-front (from a picker option) so no round-trip is needed to resolve the org.
   */
  switchLeague: (leagueId: string | null, ownerOrganizationId?: string) => void;
};

export const CurrentLeagueContext = createContext<CurrentLeagueContextType>({
  league: null,
  loading: false,
  isError: false,
  switchLeague: noop,
});
